import { primitives, tokens } from '@/styles/tokens';
import { alpha, Box, styled } from '@mui/material';

export type ToastType = 'success' | 'warning' | 'error' | 'info';

const TOAST_COLORS = {
  success: {
    bg: primitives.green[10],
    border: alpha(primitives.green[25], 0.5),
    iconColor: primitives.green[150],
    title: tokens.success.dark,
    text: primitives.green[150],
  },
  warning: {
    bg: primitives.yellow[10],
    border: alpha(primitives.yellow[25], 0.5),
    iconColor: primitives.yellow[150],
    title: primitives.yellow[150],
    text: primitives.yellow[150],
  },
  error: {
    bg: primitives.red[25],
    border: primitives.red[75],
    iconColor: primitives.red[100],
    title: primitives.red[100],
    text: primitives.red[100],
  },
  info: {
    bg: primitives.brand[25], // brand6
    border: primitives.brand[50], // brand5
    iconColor: primitives.brand[100],
    title: tokens.primary.dark,
    text: tokens.primary.main,
  },
};

export const ToastWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'type' && prop !== 'size',
})<{ type: ToastType; size?: 'small' | 'big' }>(({ type, size = 'small' }) => {
  const colors = TOAST_COLORS[type];
  return {
    display: size === 'small' ? 'flex' : 'grid',
    gridTemplateColumns: size === 'small' ? 'auto' : '38px 1fr',
    alignItems: size === 'small' ? 'center' : 'flex-start',
    padding: size === 'small' ? '8px 16px' : '16px 60px 16px 12px',
    backgroundColor: size === 'small' ? colors.bg : '#fff',
    fontWeight: 600,
    border: size === 'small' ? `1px solid ${colors.border}` : 'none',
    borderRadius: '12px',
    boxShadow:
      size === 'small'
        ? 'none'
        : type === 'success'
          ? `0 0 18px 0 ${alpha(tokens.success.main, 0.3)}`
          : `0px 5px 20px 0px ${alpha(tokens.grey[0], 0.05)}`,
    maxWidth: size === 'small' ? 'fit-content' : '400px', // Small can be auto-width
    minWidth: size === 'small' ? 'auto' : '300px',
    width: '100%',
    position: 'relative',
    gap: '12px',
    pointerEvents: 'auto',
    overflow: 'hidden',

    ...(size === 'big' &&
      type !== 'success' && {
        '&::before': {
          position: 'absolute',
          top: 0,
          left: 0,
          width: 48,
          height: '100%',
          content: '""',
          backgroundColor: colors.bg,
        },
      }),
  };
});

export const IconWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'type',
})<{ isConfetti?: boolean; type: ToastType }>(({ type, isConfetti }) => {
  const colors = TOAST_COLORS[type];
  return {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: isConfetti ? '48px' : '24px',
    height: isConfetti ? '48px' : '24px',
    margin: isConfetti ? 'auto 0 auto -12px' : 'auto 0',
    borderRadius: '50%',
    color: colors.iconColor,
    flexShrink: 0,
    zIndex: 1,
  };
});

export const Content = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: 'small' | 'big' }>(({ size }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: size === 'small' ? 'center' : 'flex-start',
  flex: 1,
  gap: '4px',
}));

export const Title = styled('div', {
  shouldForwardProp: (prop) => prop !== 'type',
})<{ type: ToastType }>(({ type }) => {
  const colors = TOAST_COLORS[type];
  return {
    fontSize: '14px',
    fontWeight: 700,
    color: colors.title,
  };
});

export const Message = styled('div', {
  shouldForwardProp: (prop) => prop !== 'type',
})<{ type: ToastType }>(({ type }) => {
  const colors = TOAST_COLORS[type];
  return {
    fontSize: '13px',
    fontWeight: 400,
    color: colors.text,
    lineHeight: '18px',
  };
});

export const CloseButton = styled('button')({
  position: 'absolute',
  top: '12px',
  right: '12px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px',
  margin: '-4px -4px 0 0',
  color: '#9CA3AF',
  transition: 'color 0.2s',
  '&:hover': {
    color: '#4B5563',
  },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});
